import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { User, Eye, Download, FileSpreadsheet, Search, Phone, Mail, MapPin, GraduationCap, Briefcase, Calendar, FileText, CheckCircle, XCircle, ChevronLeft, ChevronRight } from 'lucide-react';
import api, { getBackendUrl, getAssetUrl } from '@/utils/api';
import { eduDegree, eduInstitution, eduSpecialization, eduYear, eduPercentage, expDesignation, expCompany, expIndustry, expFromDate, expToDate, expYears } from '@/utils/seekerHelpers';

const JobSeekerManagement = () => {
  const [jobSeekers, setJobSeekers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSeeker, setSelectedSeeker] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 100;

  useEffect(() => {
    fetchJobSeekers();
  }, [statusFilter, currentPage]);

  const fetchJobSeekers = async () => {
    try {
      setLoading(true);
      let url = `/admin/job-seekers?page=${currentPage}&limit=${limit}`;
      if (statusFilter !== 'all') url += `&status=${statusFilter}`;
      
      const response = await api.get(url);
      setJobSeekers(response.data.data || []);
      setTotalPages(response.data.total_pages || 1);
      setTotal(response.data.total || 0);
    } catch (error) {
      toast.error('Failed to fetch job seekers');
    } finally {
      setLoading(false);
    }
  };

  const toggleStatus = async (userId, currentStatus) => {
    try {
      await api.put(`/admin/job-seekers/${userId}/toggle-status`);
      toast.success(`Job seeker ${currentStatus ? 'deactivated' : 'activated'} successfully`);
      fetchJobSeekers();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const viewDetails = (seeker) => {
    setSelectedSeeker(seeker);
    setShowDetailDialog(true);
  };

  const handleExport = async (format) => {
    try {
      let url = `/admin/job-seekers/export?format=${format}`;
      if (statusFilter !== 'all') url += `&status=${statusFilter}`;
      
      window.open(getBackendUrl() + '/api' + url, '_blank');
      toast.success(`Exporting to ${format.toUpperCase()}...`);
    } catch (error) {
      toast.error('Failed to export');
    }
  };

  const getImageUrl = (path) => {
    if (!path) return null;
    if (path.startsWith('http')) return path;
    return getAssetUrl(path);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const downloadCV = (cvPath) => {
    if (!cvPath) {
      toast.error('No CV uploaded');
      return;
    }
    const url = getImageUrl(cvPath);
    window.open(url, '_blank');
  };

  return (
    <div>
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
        <h1 className="text-3xl font-bold text-slate-900" style={{fontFamily: 'Outfit'}}>Job Seeker Management</h1>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleExport('csv')}
            className="text-slate-600"
            data-testid="export-csv-btn"
          >
            <Download className="w-4 h-4 mr-1" />
            CSV
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleExport('xlsx')}
            className="text-slate-600"
            data-testid="export-excel-btn"
          >
            <FileSpreadsheet className="w-4 h-4 mr-1" />
            Excel
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="p-4 mb-6 border-slate-200">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="w-full md:w-48">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger data-testid="status-filter-select">
                <SelectValue placeholder="Filter by Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </Card>

      {/* Job Seekers Grid Table */}
      <Card className="border-slate-200">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="w-12 text-center font-semibold">Sl.No</TableHead>
                <TableHead className="font-semibold">Name</TableHead>
                <TableHead className="font-semibold">Mobile</TableHead>
                <TableHead className="font-semibold">Email</TableHead>
                <TableHead className="font-semibold">State</TableHead>
                <TableHead className="font-semibold">District</TableHead>
                <TableHead className="font-semibold">City</TableHead>
                <TableHead className="text-center font-semibold">Experience</TableHead>
                <TableHead className="font-semibold">Education</TableHead>
                <TableHead className="font-semibold">Member Since</TableHead>
                <TableHead className="font-semibold">Status</TableHead>
                <TableHead className="text-center font-semibold">CV</TableHead>
                <TableHead className="text-center font-semibold">Jobs Applied</TableHead>
                <TableHead className="text-center font-semibold">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={14} className="text-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600 mx-auto mb-2"></div>
                    <p className="text-slate-600">Loading job seekers...</p>
                  </TableCell>
                </TableRow>
              ) : jobSeekers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={14} className="text-center py-12">
                    <User className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                    <p className="text-slate-600">No job seekers found</p>
                  </TableCell>
                </TableRow>
              ) : (
                jobSeekers.map((seeker, index) => (
                  <TableRow key={seeker.id} className="hover:bg-slate-50">
                    <TableCell className="text-center font-medium">{index + 1}</TableCell>
                    <TableCell>
                      <div className="font-medium text-slate-900">
                        {seeker.profile?.full_name || seeker.name || 'N/A'}
                      </div>
                    </TableCell>
                    <TableCell>{seeker.mobile}</TableCell>
                    <TableCell className="text-slate-600">{seeker.profile?.email || seeker.email || 'N/A'}</TableCell>
                    <TableCell className="text-slate-600">{seeker.profile?.state || 'N/A'}</TableCell>
                    <TableCell className="text-slate-600">{seeker.profile?.district || 'N/A'}</TableCell>
                    <TableCell className="text-slate-600">{seeker.profile?.city || 'N/A'}</TableCell>
                    <TableCell className="text-center">
                      <span className="inline-flex items-center px-2 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium">
                        {seeker.total_experience || 0} yrs
                      </span>
                    </TableCell>
                    <TableCell className="text-slate-600 text-sm max-w-[150px] truncate">
                      {seeker.highest_education || 'N/A'}
                    </TableCell>
                    <TableCell className="text-slate-600 whitespace-nowrap">
                      {formatDate(seeker.created_at)}
                    </TableCell>
                    <TableCell>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${seeker.active !== false ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                        {seeker.active !== false ? 'Active' : 'Inactive'}
                      </span>
                    </TableCell>
                    <TableCell className="text-center">
                      {seeker.profile?.cv ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => downloadCV(seeker.profile.cv)}
                          className="text-teal-600 hover:text-teal-700"
                          data-testid={`download-cv-${seeker.id}`}
                          title="Download CV"
                        >
                          <Download className="w-4 h-4" />
                        </Button>
                      ) : (
                        <span className="text-slate-400 text-xs">No CV</span>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      <span className="inline-flex items-center px-2 py-1 bg-purple-50 text-purple-700 rounded-full text-sm font-medium">
                        {seeker.applications_count || 0}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center justify-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => viewDetails(seeker)}
                          data-testid={`view-seeker-${seeker.id}`}
                          className="text-slate-600 hover:text-teal-600"
                          title="View Details"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => toggleStatus(seeker.id, seeker.active !== false)}
                          data-testid={`toggle-seeker-${seeker.id}`}
                          className={seeker.active !== false 
                            ? 'bg-red-600 hover:bg-red-700 text-white h-8 w-8 p-0' 
                            : 'bg-green-600 hover:bg-green-700 text-white h-8 w-8 p-0'}
                          title={seeker.active !== false ? 'Deactivate' : 'Activate'}
                        >
                          {seeker.active !== false ? <XCircle className="w-4 h-4" /> : <CheckCircle className="w-4 h-4" />}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
        
        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200">
            <div className="text-sm text-slate-600">
              Showing {((currentPage - 1) * limit) + 1} to {Math.min(currentPage * limit, total)} of {total} job seekers
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                <ChevronLeft className="w-4 h-4" />
                Previous
              </Button>
              <span className="text-sm text-slate-600 px-2">
                Page {currentPage} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
              >
                Next
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Job Seeker Details</DialogTitle>
          </DialogHeader>
          {selectedSeeker && (
            <div className="space-y-6">
              <div className="flex items-center gap-4">
                {selectedSeeker.profile?.profile_picture ? (
                  <img 
                    src={getImageUrl(selectedSeeker.profile.profile_picture)} 
                    alt="Profile" 
                    className="w-24 h-24 rounded-full object-cover border-4 border-slate-200" 
                  />
                ) : (
                  <div className="w-24 h-24 bg-slate-100 rounded-full flex items-center justify-center">
                    <User className="w-12 h-12 text-slate-400" />
                  </div>
                )}
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-slate-900">
                    {selectedSeeker.profile?.full_name || selectedSeeker.name || 'N/A'}
                  </h3>
                  <p className="text-slate-600">{selectedSeeker.profile?.email || selectedSeeker.email || 'N/A'}</p>
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${selectedSeeker.active !== false ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                      {selectedSeeker.active !== false ? 'Active' : 'Inactive'}
                    </span>
                    <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                      Profile: {selectedSeeker.profile?.profile_strength || 0}%
                    </span>
                    {selectedSeeker.last_login && (
                      <span className="text-xs text-slate-500">
                        Last Login: {formatDateTime(selectedSeeker.last_login)}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg">
                <div>
                  <h4 className="font-semibold text-slate-900 mb-2">Contact Information</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2">
                      <Phone className="w-4 h-4 text-slate-400" />
                      <span>{selectedSeeker.mobile}</span>
                    </div>
                    {selectedSeeker.profile?.email && (
                      <div className="flex items-center gap-2">
                        <Mail className="w-4 h-4 text-slate-400" />
                        <span>{selectedSeeker.profile.email}</span>
                      </div>
                    )}
                    {(selectedSeeker.profile?.city || selectedSeeker.profile?.state) && (
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-slate-400" />
                        <span>
                          {[selectedSeeker.profile?.city, selectedSeeker.profile?.district, selectedSeeker.profile?.state]
                            .filter(Boolean).join(', ')}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold text-slate-900 mb-2">Personal Information</h4>
                  <div className="space-y-2 text-sm">
                    <p><strong>Father's Name:</strong> {selectedSeeker.profile?.father_name || 'N/A'}</p>
                    <p><strong>Date of Birth:</strong> {selectedSeeker.profile?.dob || 'N/A'}</p>
                    <p><strong>Gender:</strong> {selectedSeeker.profile?.gender || 'N/A'}</p>
                    <p><strong>Marital Status:</strong> {selectedSeeker.profile?.marital_status || 'N/A'}</p>
                  </div>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg">
                <div>
                  <h4 className="font-semibold text-slate-900 mb-2">Summary</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2">
                      <Briefcase className="w-4 h-4 text-slate-400" />
                      <span>{selectedSeeker.total_experience || 0} years experience</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <GraduationCap className="w-4 h-4 text-slate-400" />
                      <span>{selectedSeeker.highest_education || 'N/A'}</span>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold text-slate-900 mb-2">Activity</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-slate-400" />
                      <span>Registered: {formatDate(selectedSeeker.created_at)}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4 text-slate-400" />
                      <span>Jobs Applied: {selectedSeeker.applications_count || 0}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Educational Information */}
              {selectedSeeker.profile?.education && selectedSeeker.profile.education.length > 0 && (
                <div>
                  <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                    <GraduationCap className="w-5 h-5 text-teal-600" />
                    Educational Information
                  </h4>
                  <div className="space-y-3">
                    {selectedSeeker.profile.education.map((edu, idx) => (
                      <div key={idx} className="p-4 bg-blue-50 rounded-lg border border-blue-100">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-semibold text-slate-900">{eduDegree(edu)}</p>
                            <p className="text-sm text-slate-600">{eduInstitution(edu)}</p>
                            <p className="text-sm text-slate-500">{eduSpecialization(edu)}</p>
                          </div>
                          <div className="text-right">
                            <span className="inline-flex items-center px-2 py-1 bg-white text-blue-700 rounded text-sm font-medium">
                              {eduYear(edu)}
                            </span>
                            {eduPercentage(edu) && (
                              <p className="text-sm text-slate-600 mt-1">{eduPercentage(edu)}%</p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Experience Details */}
              {selectedSeeker.profile?.experience && selectedSeeker.profile.experience.length > 0 && (
                <div>
                  <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                    <Briefcase className="w-5 h-5 text-teal-600" />
                    Experience Details
                  </h4>
                  <div className="space-y-3">
                    {selectedSeeker.profile.experience.map((exp, idx) => (
                      <div key={idx} className="p-4 bg-green-50 rounded-lg border border-green-100">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-semibold text-slate-900">{expDesignation(exp)}</p>
                            <p className="text-sm text-slate-600">{expCompany(exp)}</p>
                            {expIndustry(exp) && <p className="text-sm text-slate-500">{expIndustry(exp)}</p>}
                          </div>
                          <div className="text-right">
                            <span className="inline-flex items-center px-2 py-1 bg-white text-green-700 rounded text-sm font-medium">
                              {expYears(exp)} years
                            </span>
                            {(expFromDate(exp) || expToDate(exp)) && (
                              <p className="text-xs text-slate-500 mt-1">
                                {expFromDate(exp)} - {expToDate(exp)}
                              </p>
                            )}
                          </div>
                        </div>
                        {exp.responsibilities && (
                          <p className="text-sm text-slate-600 mt-2 pt-2 border-t border-green-200">
                            {exp.responsibilities}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {selectedSeeker.profile?.languages && selectedSeeker.profile.languages.length > 0 && (
                <div>
                  <h4 className="font-semibold text-slate-900 mb-2">Languages</h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedSeeker.profile.languages.map((lang, idx) => (
                      <span key={idx} className="px-3 py-1 bg-slate-100 text-slate-700 rounded-full text-sm">
                        {lang}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {selectedSeeker.profile?.cv && (
                <div className="flex gap-3 pt-4 border-t border-slate-200">
                  <Button
                    onClick={() => downloadCV(selectedSeeker.profile.cv)}
                    className="bg-teal-600 hover:bg-teal-700 text-white"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download CV
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default JobSeekerManagement;
